import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as ScanIcon } from 'assets/images/icons/scan.svg';
import { ReactComponent as UserDataIcon } from 'assets/images/icons/user-data.svg';

import { QUERY_PARAMS } from 'utils/constants';
import { QueryHelper, isUserCreateFormFilled } from 'utils/helpers';
import { deepEqual } from 'utils/helpers/deepEqual';
import { PAGES } from 'utils/link';

import { showModal } from 'store/Modal/Modal.action';
import { IRootStore } from 'store/Root.reducer';
import { resetUserCreateData } from 'store/UserCreate/UserCreate.action';
import {
  loadUserData,
  loadUserDataFromStorage,
  resetUserUpdateData,
  setUserUpdateStepsChanged,
} from 'store/UserUpdate/UserUpdate.action';

import { ModalType } from 'entities/common';
import { authApi } from 'services/auth';
import { parseInternationalNumber } from 'utils/countries';

import UserCreateStepOne from 'modules/UserCreate/StepOne';
import UserCreateStepTwo from 'modules/UserCreate/StepTwo';
import UserCreateSteps from 'modules/UserCreate/Steps';
import { IUserUpdateState } from 'store/UserUpdate/UserUpdate.type';

export const UserCreate: FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData, modalState } = useSelector((state: IRootStore) => ({
    userData: state[id ? 'userUpdate' : 'userCreate'],
    modalState: state.modal,
  }));
  const [storageChecked, setStorageChecked] = useState(false);
  const [initialFormData, setInitialFormData] = useState<any>(null);

  const { [QUERY_PARAMS.STEP]: pageStep } = useMemo(
    () => QueryHelper.getFromQueryString(location?.search),
    [location?.search],
  );

  const steps = useMemo(
    () => [
      { name: 'User info', icon: <UserDataIcon /> },
      { name: 'Objects', icon: <ScanIcon /> },
    ],
    [],
  );

  useEffect(
    () => () => {
      dispatch(resetUserUpdateData());
    },
    [dispatch],
  );

  useEffect(() => {
    if (id) return;
    if (Number(pageStep) === 2 && !userData.userId) {
      navigate(PAGES.USER_CREATE + QueryHelper.getQueryString({ [QUERY_PARAMS.STEP]: 1 }), {
        replace: true,
      });
    }
  }, [id, pageStep, userData.userId, navigate]);

  useEffect(() => {
    if (id) return;
    const isFormFilled = isUserCreateFormFilled(userData);

    if (isFormFilled && !location.pathname.startsWith(PAGES.USER_CREATE)) {
      dispatch(
        showModal({
          modalType: ModalType.PAGE_LEAVE,
          modalProps: {
            onConfirm: () => {
              dispatch(resetUserCreateData());
            },
          },
        }),
      );

      navigate(location.pathname + location.search, { replace: true });
    }
  }, [id, location.pathname, location.search, userData, dispatch, navigate]);

  useEffect(() => {
    if (!id) return;
    if (modalState.active && modalState.modalType !== ModalType.PAGE_LEAVE) {
      return;
    }

    const currentPath = location.pathname;
    const userUpdatePath = PAGES.USER_UPDATE.replace(':id', id || '');

    const isLeavingUserUpdatePage = !currentPath.startsWith(userUpdatePath);

    if (!isLeavingUserUpdatePage) {
      return;
    }

    if ((userData as IUserUpdateState)?.isFormChanged) {
      dispatch(
        showModal({
          modalType: ModalType.PAGE_LEAVE,
          modalProps: {
            onConfirm: () => {
              dispatch(resetUserUpdateData());
              if (id) {
                localStorage.removeItem(`userUpdateData_${id}`);
              }
              navigate(currentPath + location.search, { replace: true });
            },
          },
        }),
      );
    }
  }, [
    location.pathname,
    location.search,
    dispatch,
    navigate,
    id,
    modalState.active,
    modalState.modalType,
    userData,
  ]);

  useEffect(() => {
    if (!id) return;

    const userId = parseInt(id);

    if (userData.userId === userId) {
      setStorageChecked(true);
      return;
    }

    dispatch(resetUserUpdateData());
    dispatch(loadUserDataFromStorage(userId));
    setStorageChecked(true);
  }, [id, userData.userId, dispatch]);

  useEffect(() => {
    if (!id) return;

    setInitialFormData(null);
  }, [id]);

  useEffect(() => {
    if (userData.stepOne && userData.stepTwo && !initialFormData && !!id) {
      const currentUserId = parseInt(id);
      if (userData.userId === currentUserId && userData.stepOne.email) {
        setInitialFormData({
          stepOne: userData.stepOne,
          stepTwo: userData.stepTwo,
        });
      }
    }
  }, [userData.stepOne, userData.stepTwo, initialFormData, id]);

  useEffect(() => {
    if (initialFormData && userData.stepOne && userData.stepTwo && !!id) {
      const currentUserId = parseInt(id);

      if (userData.userId === currentUserId) {
        const stepOneChanged = !deepEqual(userData.stepOne, initialFormData.stepOne);
        const stepTwoChanged = !deepEqual(userData.stepTwo, initialFormData.stepTwo);

        const currentState = userData as IUserUpdateState;
        const prevStepOneChanged = currentState?.isStepOneChanged || false;
        const prevStepTwoChanged = currentState?.isStepTwoChanged || false;

        if (stepOneChanged !== prevStepOneChanged || stepTwoChanged !== prevStepTwoChanged) {
          dispatch(
            setUserUpdateStepsChanged({
              isStepOneChanged: stepOneChanged,
              isStepTwoChanged: stepTwoChanged,
            }),
          );
        }
      }
    }
  }, [initialFormData, userData, id, dispatch]);

  useEffect(() => {
    if (!id) return;
    if (!storageChecked) return;

    const userId = parseInt(id);

    const isDataLoadedFromStorage = (userData as any)?.isDataLoadedFromStorage;
    if (isDataLoadedFromStorage) {
      return;
    }

    if (userData.userId === userId && userData.stepOne.fullname) {
      return;
    }

    const loadUserFromAPI = async () => {
      try {
        const apiUserData = await authApi.getUser(userId);

        const parsedPhone = apiUserData.whatsapp_number
          ? parseInternationalNumber(apiUserData.whatsapp_number)
          : null;

        const stepOne = {
          fullname: apiUserData.fullname || '',
          email: apiUserData.email,
          password: '',
          phoneCode: parsedPhone?.iso2 || 'ae',
          phoneNumber: parsedPhone?.localNumber || '',
          whatsappNotifications:
            apiUserData.whatsapp_notifications_enabled ?? !!apiUserData.whatsapp_number,
        };

        const mappedDetails =
          apiUserData.objects
            ?.filter((obj: any) => !obj.is_deleted)
            .map((obj) => {
              const objectSensors = ((obj as any).sensors || [])
                .filter((sensor: any) => !sensor.is_deleted)
                .map((sensor: any) => ({
                  id: sensor.id,
                  type: sensor.sensor_type,
                  sensorEui: sensor.sensor_eui,
                  sensorKey: sensor.device_code ?? '',
                  active: sensor.active ?? true,
                }));

              return {
                id: obj.id,
                name: obj.name,
                file: undefined,
                originalFilename: obj.original_filename,
                fileId: obj.file_id,
                zones: obj.zones
                  .filter((zone: any) => !zone.is_deleted)
                  .map((zone: any) => ({
                    id: zone.id,
                    name: zone.name,
                    plantType: zone.plant_type || '',
                    plantName: zone.plant_name || '',
                    soilType: zone.soil_type || '',
                    photoOriginalFilename: zone.photo_original_filename,
                    photoFileId: zone.photo_file_id,
                    photoUrl: zone.photo_url,
                    sensors: (zone.sensors || [])
                      .filter((sensor: any) => !sensor.is_deleted)
                      .map((sensor: any) => ({
                        id: sensor.id,
                        type: sensor.sensor_type,
                        sensorEui: sensor.sensor_eui,
                        sensorKey: sensor.device_code ?? '',
                        active: sensor.active ?? true,
                      })),
                  })),
                sensors: objectSensors,
              };
            }) || [];

        const defaultDetails = [
          {
            name: '',
            file: undefined,
            zones: [
              {
                name: '',
                plantType: '',
                plantName: '',
                soilType: '',
                sensors: [],
              },
            ],
            sensors: [],
          },
        ];

        const stepTwo = {
          details: mappedDetails.length > 0 ? mappedDetails : defaultDetails,
        };

        dispatch(
          loadUserData({
            userId,
            stepOne,
            stepTwo,
          }),
        );
      } catch (error) {
        console.error('Failed to load user data:', error);
      }
    };

    if (userData.userId !== userId || !userData.stepOne.fullname) {
      loadUserFromAPI();
    }
  }, [id, storageChecked, userData, dispatch]);

  return (
    <div className="user-create">
      <UserCreateSteps steps={steps} pageStep={pageStep} />

      {Number(pageStep) === 1 && <UserCreateStepOne id={id} />}

      {Number(pageStep) === 2 && (!id || userData.userId === parseInt(id)) && (
        <UserCreateStepTwo id={id} />
      )}
    </div>
  );
};
